import { useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'components/common'
import Modal from 'components/modal'
import { CronJob } from 'types/cronJob'
import { resetCronJob } from 'utils/api/cronJobsApi'
import { handleError } from 'utils/helpers/handleError'

type Props = {
  job?: CronJob
  closeModal: () => void
  onDone: () => void
}

// Confirm popup for "Reset", with a warning that a live run is not stopped
const ResetCronJobModal = ({ job, closeModal, onDone }: Props) => {
  const [loading, setLoading] = useState(false)

  if (!job) return null

  const jobLabel = job.title ?? job._id

  const confirm = async () => {
    setLoading(true)
    const toastId = toast.loading(`Resetting ${jobLabel}...`)

    try {
      const { data } = await resetCronJob(job._id)
      toast.success(data?.message ?? 'Job reset', { id: toastId })
      closeModal()
      onDone()
    } catch (error) {
      handleError(error, toastId)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      width={550}
      title={`Reset "${jobLabel}"?`}
      titleClassname="text-xl font-semibold max-w-xl"
      isOpen
      closeModal={closeModal}
      customStyle={{ padding: '25px 40px' }}
    >
      <>
        <p>
          Only use this when the job is stuck. It is set back to idle, so it can
          run again on its next schedule or manual trigger. If the old run is
          still working, it keeps going in the background and its result is
          ignored.
        </p>

        <div className="mt-6 flex w-full items-center gap-5">
          <Button
            onClick={confirm}
            className="w-full"
            buttonText="Confirm"
            type="button"
            loading={loading}
          />
          <Button
            className="w-full"
            buttonText="Cancel"
            variant="outline"
            type="button"
            onClick={closeModal}
          />
        </div>
      </>
    </Modal>
  )
}

export default ResetCronJobModal
